// Layouts
import React, { useContext, useEffect, useCallback, useMemo } from 'react';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { Platform, StyleSheet } from 'react-native';
import { MuseumApp } from '../layouts/museum/MuseumAppRoutes';
import { PlaceApp } from '../layouts/place/PlaceAppRoutes';
import { PlaceText } from '@/layouts/genericPages/components/PlaceText';
import Toast from 'react-native-toast-message';
import { FindifyToastConfig } from '@/layouts/genericPages/components/FindifyToast';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { NavigationContainer, createNavigationContainerRef } from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { InitialPage } from "./InitialPage";
import { InitialPagePlacesList } from "./InitialPagePlaceList";
import { ScanQrTab } from "@/layouts/genericPages/ScanQr";
import { EventsApp } from '@/layouts/events/EventsAppRoutes';
import { FindifyDataContext, FindifyDataContextType } from '@/contexts/FindifyDataContext';
import Config from 'react-native-config';
import { MobileWorkspacesApi } from "../findifyv2-api-ts/mobile/workspaces/WorkspacesApi";
import { FindifyLoadingScreen } from '../layouts/genericPages/components/FindifyLoadingScreen';
import { View } from 'react-native';
import { getWorkspaceTitle } from '@/layouts/genericPages/helpers/WorkspaceText';
import { PwaInstallPrompt } from './PwaInstallPrompt';

const Stack = createNativeStackNavigator();

const WorkspaceWrapper = () => {
  const findifyDataContext = useContext(FindifyDataContext);

  if (findifyDataContext?.visitWorkspaceSelected === false && findifyDataContext?.hasWorkspaceError) {
    return (
      <View style={styles.centerContainer}>
        <PlaceText>Error loading workspace. Please check your connection.</PlaceText>
      </View>
    );
  }

  if (!findifyDataContext?.visitWorkspaceSelected) {
    return <FindifyLoadingScreen />;
  }

  const theme = findifyDataContext.workspace?.app_settings?.app_theme;

  switch (theme) {
    case 'museum':
      return <MuseumApp />;
    case 'events':
      return <EventsApp />;
    case 'default':
      return <PlaceApp />;
    default:
      return (
        <View style={styles.centerContainer}>
          <PlaceText>Coming soon: Theme {String(theme ?? 'undefined')}</PlaceText>
        </View>
      );
  }
};

const AppWithoutProviders = (): JSX.Element => {
  const findifyDataContext = useContext(FindifyDataContext);
  const navigationRef = useMemo(() => createNavigationContainerRef<any>(), []);
  console.log("AppWithoutProviders render:", {
    workspaceId: Config.WORKSPACE_ID,
    visitWorkspaceSelected: findifyDataContext?.visitWorkspaceSelected,
    isStorageLoading: findifyDataContext?.isStorageLoading
  });

  const loadConfiguredWorkplace = useCallback(async (context: FindifyDataContextType) => {
    const mobileWorkspacesApi = new MobileWorkspacesApi();

    const filters = {
      published_app_eq: 1,
      workspace_id: Config.WORKSPACE_ID
    };

    try {
      const data = await mobileWorkspacesApi.getList(0, 100, null, filters, null);
      if (data && data.results && data.results.length > 0) {
        context.setAvailableWorkspaces(data.results);
        context.setWorkspace(data.results[0]);
        context.setVisitWorkspaceSelected(true);
        context.setHasWorkspaceError(false);
      } else {
        context.setHasWorkspaceError(true);
      }
    } catch (error) {
      console.error("Failed to load configured workplace:", error);
      context.setHasWorkspaceError(true);
    }
  }, []);

  useEffect(() => {
    if (Config.WORKSPACE_ID && findifyDataContext && !findifyDataContext.isStorageLoading && !findifyDataContext.visitWorkspaceSelected) {
      loadConfiguredWorkplace(findifyDataContext);
    }
  }, [findifyDataContext?.isStorageLoading, findifyDataContext?.visitWorkspaceSelected, loadConfiguredWorkplace]);

  useEffect(() => {
    if (Platform.OS !== 'web' || !findifyDataContext?.visitWorkspaceSelected || typeof window === 'undefined') return;

    const pushGuardState = () => {
      window.history.pushState({ findifyBackGuard: true }, document.title, window.location.href);
    };

    pushGuardState();

    const handleBrowserBack = () => {
      if (navigationRef.isReady() && navigationRef.canGoBack()) {
        navigationRef.goBack();
      }

      // Keep one browser-history entry ahead of the app so Android/browser back
      // does not close the installed PWA when the internal stack is at root.
      setTimeout(pushGuardState, 0);
    };

    window.addEventListener('popstate', handleBrowserBack);
    return () => window.removeEventListener('popstate', handleBrowserBack);
  }, [findifyDataContext?.visitWorkspaceSelected, navigationRef]);

  useEffect(() => {
    if (Platform.OS !== 'web' || !findifyDataContext?.workspace || typeof document === 'undefined') return;

    const appName = getWorkspaceTitle(findifyDataContext);
    document.title = appName;

    let appleTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]') as HTMLMetaElement | null;
    if (!appleTitle) {
      appleTitle = document.createElement('meta');
      appleTitle.name = 'apple-mobile-web-app-title';
      document.head.appendChild(appleTitle);
    }
    appleTitle.content = appName;

    // The install name is read from the Web App Manifest. Recreate it after
    // the Workspace is loaded so a single PWA build follows its live name.
    const manifestLink = document.querySelector('link[rel="manifest"]') as HTMLLinkElement | null;
    if (!manifestLink || !window.URL?.createObjectURL) return;

    const originalManifestUrl = manifestLink.href;
    const manifestUrl = window.URL.createObjectURL(new Blob([JSON.stringify({
      name: appName,
      short_name: appName,
      description: findifyDataContext.workspace?.description?.[findifyDataContext.language] ?? appName,
      id: '/',
      start_url: '/',
      scope: '/',
      display: 'standalone',
      orientation: 'portrait',
      background_color: '#fff1e6',
      theme_color: '#b2700d',
      icons: [
        { src: '/icons/icon-192.png', sizes: '192x192', type: 'image/png', purpose: 'any' },
        { src: '/icons/icon-512.png', sizes: '512x512', type: 'image/png', purpose: 'any' },
        { src: '/icons/icon-maskable-512.png', sizes: '512x512', type: 'image/png', purpose: 'maskable' },
      ],
    })], { type: 'application/manifest+json' }));
    manifestLink.href = manifestUrl;

    return () => {
      manifestLink.href = originalManifestUrl;
      window.URL.revokeObjectURL(manifestUrl);
    };
  }, [findifyDataContext?.workspace, findifyDataContext?.language]);

  const mainNavigator = useMemo(() => {
    if (!findifyDataContext || findifyDataContext.isStorageLoading) {
      return <FindifyLoadingScreen />;
    }

    return (
      <NavigationContainer ref={navigationRef}>
        <Stack.Navigator
          screenOptions={{
            animation: 'fade',
            animationTypeForReplace: 'pop',
            animationDuration: 100,
            headerShown: false
          }}
        >
          {!findifyDataContext.visitWorkspaceSelected && !Config.WORKSPACE_ID && (
            <Stack.Group>
              <Stack.Screen name="InitialPage" component={InitialPage} />
              <Stack.Screen name="InitialPagePlaceList" component={InitialPagePlacesList} />
              <Stack.Screen name="ScanQR" component={ScanQrTab} />
            </Stack.Group>
          )}
          {(findifyDataContext.visitWorkspaceSelected || Config.WORKSPACE_ID) && (
            <Stack.Group>
              <Stack.Screen name="WorkspacePage" component={WorkspaceWrapper} />
            </Stack.Group>
          )}
        </Stack.Navigator>
      </NavigationContainer>
    );
  }, [findifyDataContext?.isStorageLoading, findifyDataContext?.visitWorkspaceSelected]);

  return (
    <SafeAreaProvider>
      <GestureHandlerRootView style={styles.root}>
        {mainNavigator}
        <PwaInstallPrompt
          workspaceName={findifyDataContext?.workspace ? getWorkspaceTitle(findifyDataContext) : ''}
        />
        <Toast config={FindifyToastConfig} />
      </GestureHandlerRootView>
    </SafeAreaProvider>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#fff',
    display: 'flex',
  },
});

export default AppWithoutProviders;
